import {CFAttrTO, CustomFieldTO} from 'generated/JavaTO'
import {CFMember, CFModel, CFSetOptions, CFValue, isCFMember} from 'module/CustomField/type/CFType'
import {CF} from 'module/CustomField/util/CustomField'
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {transformCFData} from 'module/CustomField/util/transformCFData'
import {ClipboardMap} from 'module/Clipboard/type/ClipboardTypes'

export enum PastePhase {
	INIT, CHANGE, ADD_CF
}

export const PasteCFAttrKeyMap = {
	[PastePhase.INIT]: [CFAttrKey.ClipboardPaste],
	[PastePhase.CHANGE]: [CFAttrKey.ClipboardPasteOnChange, CFAttrKey.ClipboardPasteOnChangeOnlyInit],
	[PastePhase.ADD_CF]: [CFAttrKey.ClipboardPasteOnChange, CFAttrKey.ClipboardPasteOnChangeOnlyInit, CFAttrKey.ClipboardPaste],
}

interface PasteAction {
	cfDef: CustomFieldTO
	model: CFModel
	value: CFValue | CFMember
	options?: CFSetOptions
}

interface PasteParams {
	clipName: string
	transformString?: string
	dependentClipNames: string[]
	singleIndex: number
	overwrite: boolean
}

export function parsePasteClipboardCFAttrValue(cfAttr: CFAttrTO, cfDef: CustomFieldTO): PasteParams {
	const {key, type, value} = cfAttr
	const separatorIndexTransformString = value.indexOf(':')
	let clipName: string
	let dependentClipNames: string[]
	let transformString: string | undefined
	if (separatorIndexTransformString < 0) {
		dependentClipNames = value.split(' ')
		clipName = dependentClipNames[0] || ''
	} else {
		dependentClipNames = value.substring(0, separatorIndexTransformString).split(' ')
		clipName = dependentClipNames[0] || ''
		transformString = value.substring(separatorIndexTransformString + 1)
	}
	if (!transformString) {
		transformString = cfDef.cfAttrs.filter(attr => attr.key === CFAttrKey.transformCFData && (!attr.type || +attr.type === cfAttr.id))
			.sort((a, b) => {
				if (a.type && b.type) return a.type.localeCompare(b.type);
				if (a.type) return -1; // a ist ein String, b ist null/undefined -> a kommt zuerst
				if (b.type) return 1;  // b ist ein String, a ist null/undefined -> b kommt zuerst
				return 0;              // beide sind null/undefined
			})[0]?.value
	}
	const singleIndex = parseInt(type || '')
	return {clipName, transformString, dependentClipNames, singleIndex, overwrite: key === CFAttrKey.ClipboardPasteOnChange}
}

export function pasteClipboardFromCFAttr(pastePhase: PastePhase, cfDefs: CustomFieldTO[], clipboard: ClipboardMap, model: CFModel, alwaysOverwrite: boolean): PasteAction[] {
	return cfDefs.flatMap(cfDef => {
		const paramList = cfDef.cfAttrs.flatMap(cfAttr => {
			if (!cfAttr.key || !(PasteCFAttrKeyMap[pastePhase] as string[]).includes(cfAttr.key)) return [];
			return parsePasteClipboardCFAttrValue(cfAttr, cfDef)
		})
		return paramList.flatMap(params => pasteClipboardFromParsedParams(params, cfDef, cfDefs, clipboard, model, alwaysOverwrite))
	})
}
export function pasteClipboardFromParsedParams(params: PasteParams, cfDef: CustomFieldTO, cfDefs: CustomFieldTO[], clipboard: ClipboardMap, model: CFModel, alwaysOverwrite?: boolean): PasteAction[] {
	const {clipName, transformString, singleIndex, overwrite} = params
	let clipValues = clipboard[clipName]
	const isSingleIndex = Boolean(singleIndex || singleIndex === 0)
	const isArray = Array.isArray(clipValues)
	let cfs: (CFValue | CFMember)[];
	let pos = 0
	const positions = CF.getPositions([cfDef], model).sort((a, b) => a === b ? 0 : a === undefined ? -1 : b === undefined ? 1 : a - b)
	if (isSingleIndex) {
		// exact pos set
		cfs = Array.isArray(clipValues) ? [clipValues[0]] : [clipValues] // only single value clipboards allowed
		let posToSet = positions[singleIndex]
		if (!posToSet && posToSet !== 0) posToSet = singleIndex
		pos = posToSet
	} else if (!isArray) {
		// no array -> fill existing cf with values
		cfs = positions.map((x) => ({
			data: isCFMember(clipValues) ? clipValues.data : clipValues as CFValue,
			pos: x || 0,
			dstField: cfDef.dstField,
		}))
	} else {
		// arrays get exactly set
		cfs = clipValues
	}
	return cfs.flatMap((v: CFValue | CFMember) => {
		let data
		if (isCFMember(v)) {
			data = v.data
			if (v.pos || v.pos === 0) {
				pos = (v as CFMember).pos
			}
		} else {
			data = v
		}
		if (transformString) {
			data = transformCFData(transformString, data, pos, clipboard, model, cfDef, cfDefs)
		}
		if (data === undefined) return []
		let ret: PasteAction[];
		if (alwaysOverwrite || overwrite || CF.isNullOrDefault(cfDef, model, pos)) {
			ret = [{
				cfDef,
				model,
				value: data,
				options: {pos},
			}]
		} else {
			ret = []
		}
		pos++
		return ret
	})
}